<template>
  <div class="mph-root">
    <div class="mph-card shadow rounded-lg">
      <div v-if="user_loading" class="mph-card__inner mph-card__inner--loading">
        <div class="mph-identity-band">
          <div class="mph-identity">
          <div class="mph-skeleton mph-skeleton--avatar"></div>
          <div class="mph-skeleton-block">
            <div class="mph-skeleton mph-skeleton--title"></div>
            <div class="mph-skeleton mph-skeleton--subtitle"></div>
            <div class="mph-skeleton mph-skeleton--line"></div>
          </div>
          </div>
        </div>
        <div class="mph-chips mph-chips--loading">
          <div class="mph-skeleton mph-skeleton--chip"></div>
          <div class="mph-skeleton mph-skeleton--chip"></div>
          <div class="mph-skeleton mph-skeleton--chip"></div>
        </div>
      </div>

      <div v-else-if="user" class="mph-card__inner">
        <div class="mph-identity-band">
          <div class="mph-identity">
          <div class="mph-media">
            <button
              type="button"
              class="mph-avatar-btn"
              :class="{ 'mph-avatar-btn--empty': !visume }"
              :aria-label="visume ? 'Open visume' : 'Record visume'"
              @click.prevent="$emit('open-visume')"
            >
              <template v-if="visume">
                <video
                  :key="visumeVideoSrc"
                  class="mph-avatar-btn__video"
                  :src="visumeVideoSrc"
                  preload="metadata"
                  muted
                  playsinline
                  webkit-playsinline
                  @loadedmetadata="primeVisumeFrame"
                  @loadeddata="primeVisumeFrame"
                />
                <span class="mph-avatar-btn__play" aria-hidden="true">
                  <span class="mph-avatar-btn__play-icon">
                    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
                      <path fill="currentColor" d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </span>
                <span class="mph-avatar-btn__badge" aria-hidden="true">
                  <i class="mdi mdi-video"></i> Visume
                </span>
              </template>
              <template v-else>
                <span class="mph-avatar-btn__record-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14v-4z" />
                    <rect x="3" y="6" width="12" height="12" rx="2" ry="2" />
                    <circle cx="9" cy="12" r="2" fill="currentColor" stroke="none" />
                  </svg>
                </span>
                <span class="mph-avatar-btn__add-label">Record visume</span>
              </template>
            </button>
          </div>

          <div class="mph-info">
            <div class="mph-info__row">
              <div class="mph-info__main">
                <h1 class="mph-name">{{ user.name }}</h1>
                <p v-if="designation" class="mph-role">{{ designation }}</p>
              </div>
            </div>

            <div class="mph-contact">
              <span class="mph-contact__item">
                <i class="mdi mdi-email-outline" aria-hidden="true"></i>
                {{ user.email }}
                <button
                  v-if="!user.email_verified"
                  type="button"
                  class="mph-badge mph-badge--danger"
                  @click="$emit('verify-email')"
                >Verify email</button>
              </span>
              <span class="mph-contact__item">
                <i class="mdi mdi-phone-outline" aria-hidden="true"></i>
                {{ user.mobile }}
              </span>
            </div>

            <div v-if="publicProfilePath" class="mph-public-link">
              <a
                class="mph-public-link__open"
                :href="publicProfilePath"
                target="_blank"
                rel="noopener noreferrer"
                title="Open your public profile in a new tab"
              >
                <span class="mph-public-link__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </span>
                <span class="mph-public-link__text">
                  <span class="mph-public-link__label">Public profile</span>
                  <span class="mph-public-link__url">
                    <span class="mph-public-link__host">{{ publicProfileHost }}</span><span class="mph-public-link__path">/candidate/{{ publicProfileSlug }}</span>
                  </span>
                </span>
                <span class="mph-public-link__external" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </span>
              </a>
              <button
                type="button"
                class="mph-public-link__copy"
                :title="linkCopied ? 'Copied!' : 'Copy public profile link'"
                :aria-label="linkCopied ? 'Link copied' : 'Copy public profile link'"
                @click.prevent="copyPublicLink"
              >
                <svg v-if="linkCopied" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                <span class="mph-public-link__copy-label">{{ linkCopied ? 'Copied' : 'Copy' }}</span>
              </button>
            </div>
          </div>
          </div>
        </div>

        <ul class="mph-chips">
          <li
            class="mph-chip mph-chip--action"
            :class="profileCompletionMetaClass"
            role="button"
            tabindex="0"
            :title="profileCompletionHint"
            @click.prevent="$emit('open-completion')"
            @keyup.enter.prevent="$emit('open-completion')"
          >
            <i class="mdi mdi-chart-arc" aria-hidden="true"></i>
            <span>Profile · {{ profileCompletionPercent }}%</span>
          </li>
          <li v-if="profileLocationLabel" class="mph-chip">
            <i class="mdi mdi-map-marker-outline" aria-hidden="true"></i>
            <span>{{ profileLocationLabel }}</span>
          </li>
          <li v-if="educationLabel" class="mph-chip">
            <i class="mdi mdi-school-outline" aria-hidden="true"></i>
            <span>{{ educationLabel }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="visume-action-card shadow rounded-lg" v-if="showVisumeStatusCard">
      <div class="visume-action-card__inner">
        <div class="visume-action-card__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14v-4z" />
            <rect x="3" y="6" width="12" height="12" rx="2" ry="2" />
          </svg>
        </div>
        <div class="visume-action-card__content">
          <span class="visume-action-card__label">Video profile</span>
          <p class="visume-action-card__title">
            <template v-if="visumeMissing">{{ visumeStatusCardHint }}</template>
            <template v-else>
              <span class="text-capitalize">{{ user.name }}</span>, {{ visumeStatusCardTitle }}
              <span class="visume-action-card__hint">— {{ visumeStatusCardHint }}</span>
            </template>
          </p>
          <div v-if="!visumeMissing" class="visume-action-card__progress-row">
            <div
              class="visume-action-card__progress"
              role="progressbar"
              :aria-valuenow="visumeDurationSeconds"
              aria-valuemin="0"
              :aria-valuemax="visumeDurationTarget"
              :aria-label="`Visume length: ${visumeDurationSeconds} of ${visumeDurationTarget} seconds`"
            >
              <div class="visume-action-card__progress-bar" :style="{ width: visumeDurationPercent + '%' }"></div>
            </div>
            <span class="visume-action-card__progress-label">{{ visumeDurationSeconds }}s / {{ visumeDurationTarget }}s</span>
          </div>
          <button
            v-if="visumeMissing"
            type="button"
            class="visume-action-card__example"
            @click.prevent="openVisumeExample"
          >See example</button>
        </div>
        <button type="button" class="visume-action-card__cta" @click.prevent="$emit('open-visume')">{{ visumeBannerCtaLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import profileCompletionMixin from '@/mixins/profileCompletionMixin';
import { buildPublicProfileSlug } from '@/utils/publicProfile';

export default {
  name: 'ProfileHeader',
  mixins: [profileCompletionMixin],
  props: {
    user: { type: Object, default: () => ({}) },
    user_loading: { type: Boolean, default: true },
    user_city: { type: Object, default: null },
    user_state: { type: String, default: null },
    designation: { type: String, default: null },
    visume: { type: String, default: '' },
    visume_path: { type: String, default: '' },
    resume_path: { type: String, default: null },
  },
  data() {
    return {
      linkCopied: false,
    };
  },
  computed: {
    publicProfileSlug() {
      return buildPublicProfileSlug(this.user);
    },
    publicProfilePath() {
      return this.publicProfileSlug ? `/candidate/${this.publicProfileSlug}` : '';
    },
    publicProfileUrl() {
      if (!this.publicProfilePath) return '';
      if (typeof window !== 'undefined') {
        return `${window.location.origin}${this.publicProfilePath}`;
      }
      return this.publicProfilePath;
    },
    publicProfileHost() {
      if (typeof window !== 'undefined') {
        return window.location.host;
      }
      return 'hireclap.com';
    },
    visumeVideoSrc() {
      const path = this.visume || this.visume_path;
      if (!path) return '';
      const clean = String(path).split('#')[0];
      return `${this.$S3_bucket_url}${clean}`;
    },
  },
  methods: {
    primeVisumeFrame(event) {
      const video = event && event.target;
      if (!video) return;
      try {
        if (video.readyState >= 1) {
          video.currentTime = 0.1;
        }
      } catch {
        /* iOS may reject seek before metadata */
      }
    },
    openVisumeExample() {
      window.open('https://www.youtube.com/watch?v=Sv6fKLMmCSo', '_blank', 'noopener,noreferrer');
    },
    async copyPublicLink() {
      if (!this.publicProfileUrl) return;
      try {
        await navigator.clipboard.writeText(this.publicProfileUrl);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } catch {
        const input = document.createElement('textarea');
        input.value = this.publicProfileUrl;
        input.setAttribute('readonly', '');
        input.style.position = 'absolute';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      }
    },
  },
};
</script>
